import { collabDatabaseBatch } from '@databases/collab-state-manager/batch';
import { FieldValueEdit } from '@databases/fields/FieldValueEdit';
import { type DefaultRootState, defaultStoreUtils } from '@databases/redux';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import { type DatabaseValue } from '@databases/types/database';
import { DatabaseChangeType } from '@databases/types/redux';
import {
  type DatabaseChange,
  type ValueChange,
} from '@databases/utils/database/changesets';
import { getInitialValue } from '@databases/utils/database/field-types';
import {
  getDatabaseEntryOptional,
  getDatabaseFieldOptional,
  getDatabaseValue,
} from '@databases/utils/database/utils';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import { batch } from '@databases/utils/redux/redux';
import React, { forwardRef, useCallback } from 'react';

import { getBulkEditDateValueForEntry } from './bulk-edit-date-value';

type DatabaseValueBulkEditProps = {
  contentId: string;
  value: DatabaseValue | undefined;
  onBulkEditChange: (value: DatabaseValue, change: ValueChange | null) => void;
  selectedFieldId: string;
  selectedEntryIds: string[];
};

const DatabaseValueBulkEdit = forwardRef<HTMLInputElement, DatabaseValueBulkEditProps>(
  function DatabaseValueBulkEdit(
    { contentId, value, onBulkEditChange, selectedFieldId, selectedEntryIds },
    ref
  ) {
    const [firstSelectedField, firstSelectedEntry, selectedValuesByEntryId] =
      defaultStoreUtils.useEqualSelector((state: DefaultRootState) => {
        const database = selectDatabaseByContentIdRequired(state, {
          contentId,
        });
        const firstSelectedField = getDatabaseFieldOptional(database, selectedFieldId);
        const firstSelectedEntry = getDatabaseEntryOptional(
          database,
          selectedEntryIds[0]
        );

        return [
          firstSelectedField,
          firstSelectedEntry,
          selectedEntryIds.map((entryId) => {
            const entry = getDatabaseEntryOptional(database, entryId);

            return [
              entryId,
              firstSelectedField && entry
                ? getDatabaseValue(database, firstSelectedField, entry)
                : undefined,
            ] as const;
          }),
        ] as const;
      });

    const dispatch = defaultStoreUtils.useDispatch();
    const handleAddChangeToDraft = useCallback(
      (change: DatabaseChange) => {
        dispatch(addChangeToDraft(contentId, change));
      },
      [contentId, dispatch]
    );

    const store = defaultStoreUtils.useStore();
    const handleBulkEditValues = useCallback(
      (value: DatabaseValue, change: ValueChange | null) => {
        if (!firstSelectedField) {
          return;
        }
        onBulkEditChange(value, change);
        batch(() => {
          collabDatabaseBatch(store, contentId, () => {
            const selectedValuesByEntryIdMap = new Map(selectedValuesByEntryId);
            selectedEntryIds.forEach((entryId) => {
              const valueForEntry = getBulkEditDateValueForEntry({
                bulkEditValue: value,
                existingValue: selectedValuesByEntryIdMap.get(entryId),
                field: firstSelectedField,
              });

              handleAddChangeToDraft({
                type: DatabaseChangeType.SET_VALUE,
                fieldId: firstSelectedField.id,
                entryId: entryId,
                value: valueForEntry.value,
                change: valueForEntry.change ?? change,
              });
            });
          });
        });
      },
      [
        contentId,
        handleAddChangeToDraft,
        onBulkEditChange,
        selectedEntryIds,
        selectedValuesByEntryId,
        firstSelectedField,
        store,
      ]
    );

    if (!firstSelectedField) {
      return null;
    }

    return (
      <FieldValueEdit
        contentId={contentId}
        field={firstSelectedField}
        entryId={firstSelectedEntry?.id}
        value={value || getInitialValue(firstSelectedField.type)}
        isSeamless
        onChange={handleBulkEditValues}
        autoFocus
        addChangeToDraft={handleAddChangeToDraft}
        ref={ref}
        isBulkEdit={true}
      />
    );
  }
);

export default DatabaseValueBulkEdit;
